import type { ApiClient } from '@carbonwebmail/api-client'
import type { CampaignReportDto } from '@carbonwebmail/contracts'
import { useCallback } from 'react'
import type { AsyncState } from '../../ui/Async.js'
import { useLoaded } from '../../ui/useLoaded.js'

/**
 * What happened to one campaign.
 *
 * Loaded once rather than polled. A send takes hours, so a figure that creeps
 * upward on its own would have somebody watching a screen instead of getting on
 * with their day — and the refresh is one press when they do want it.
 */

export type CampaignReportState = AsyncState<CampaignReportDto>

export function useCampaignReport({
  client,
  campaignId,
}: {
  readonly client: ApiClient
  readonly campaignId: string
}) {
  const load = useCallback(() => client.campaignReport(campaignId), [client, campaignId])
  const { state, reload } = useLoaded<CampaignReportDto>(
    load,
    'We could not load that campaign. Please try again in a moment.',
  )

  return { state, refresh: reload }
}

/**
 * Whether the workers still have something to do.
 *
 * `unknown` and `dead` are not outstanding: neither is ever retried, one
 * because the provider's answer was ambiguous (I7) and the other because the
 * retries are spent. Counting them as remaining would leave a campaign showing
 * work in progress forever.
 */
export function stillSending(queue: CampaignReportDto['queue']): boolean {
  return queue.queued > 0 || queue.claimed > 0
}
